import { put, call, takeLatest } from "redux-saga/effects";
import * as Actions from "us.collection.debtor/actions";
import * as API from "us.collection.debtor/services";
import { AdditionalInformation } from "us.collection.debtor/constants/Actions";
import { formatAdditionalInfoMetaData } from "us.collection.debtor/functions";
import _ from "lodash";
import Common from "us.common";
import { i18n } from "us.helper";
import { BMD } from "us.collection/services/BMD";

const { $MessageBox, $Message } = Common.Components;

const { additionalInformation } = Actions;
const { get, add, edit, deleteInfo, getMetaData } = API.additionalInformation;
const {
  informationList,
  newInformation,
  informationDetails,
  deleteInformation,
  informationMetaData,
  bmdDataTypeValues,
} = additionalInformation;
const {
  GET_ADDITIONAL_INFORMATION,
  NEW_ADDITIONAL_INFORMATION,
  UPDATE_ADDITIONAL_INFORMATION,
  DELETE_ADDITIONAL_INFORMATION,
  GET_INFORMATION_META_DATA,
  GET_DATA_TYPE_VALUES,
} = AdditionalInformation;

export const AdditionalInformationSagas = {
  information: {
    get: function* (action: any): any {
      const params = action?.payload?.data ?? {};
      try {
        const { data, status } = yield call(get, params);
        if (Array.isArray(data) && status === 200) {
          yield put(informationList.success(data));
        } else {
          yield put(informationList.fail(new Error()));
        }
      } catch (error) {
        yield put(informationList.fail(new Error()));
      }
    },
    new: function* (action: any): any {
      const params = action?.payload?.data ?? {};
      try {
        const { data, status } = yield call(add, params);
        if (status === 200) {
          yield put(newInformation.success(data));
          $MessageBox(
            "success",
            "US.COLLECTION.DEBTOR:ADDITIONAL_INFORMATION.ADDITIONAL_INFORMATION_HAS_BEEN_ADDED_SUCCESSFULLY",
            "",
            ""
          );

          if (informationList.get)
            yield put(informationList.get(params?.debtorNo));
          if (informationList.openDrawer)
            yield put(informationList.openDrawer({}));
        } else {
          yield put(newInformation.fail(new Error()));
          $MessageBox(
            "error",
            "US.COLLECTION.DEBTOR:ADDITIONAL_INFORMATION.ADD_ADDITIONAL_INFORMATION_FAILED",
            "",
            ""
          );
        }
      } catch (error) {
        yield put(newInformation.fail(new Error()));
        $MessageBox(
          "error",
          "US.COLLECTION.DEBTOR:ADDITIONAL_INFORMATION.ADD_ADDITIONAL_INFORMATION_FAILED",
          "",
          ""
        );
      }
    },
    update: function* (action: any): any {
      const params = action?.payload?.data ?? {};
      const { saveParams } = params ?? {};
      try {
        const { data, status } = yield call(edit, params);
        if (status === 200) {
          yield put(informationDetails.success(data));
          $MessageBox(
            "success",
            "US.COLLECTION.DEBTOR:ADDITIONAL_INFORMATION.ADDITIONAL_INFORMATION_HAS_BEEN_UPDATED_SUCCESSFULLY",
            "",
            ""
          );

          if (informationList.get)
            yield put(informationList.get(saveParams?.debtorNo));
          if (informationList.openDrawer)
            yield put(informationList.openDrawer({}));
        } else {
          yield put(informationDetails.fail(new Error()));
          $MessageBox(
            "error",
            "US.COLLECTION.DEBTOR:ADDITIONAL_INFORMATION.UPDATE_ADDITIONAL_INFORMATION_FAILED",
            "",
            ""
          );
        }
      } catch (error) {
        yield put(informationDetails.fail(new Error()));
        $MessageBox(
          "error",
          "US.COLLECTION.DEBTOR:ADDITIONAL_INFORMATION.UPDATE_ADDITIONAL_INFORMATION_FAILED",
          "",
          ""
        );
      }
    },
    delete: function* (action: any): any {
      const params = action?.payload?.data ?? {};
      $Message.loading(i18n.t("COMMON.DELETING") as string, 0);

      try {
        const { data, status } = yield call(deleteInfo, params);
        if (status === 200) {
          yield put(deleteInformation.success(data));
          $Message.destroy();
          $MessageBox(
            "success",
            "US.COLLECTION.DEBTOR:ADDITIONAL_INFORMATION.ADDITIONAL_INFORMATION_HAS_BEEN_DELETED_SUCCESSFULLY",
            "",
            ""
          );

          if (informationList.get)
            yield put(informationList.get(params?.debtorNo));
        } else {
          yield put(deleteInformation.fail(new Error()));
          $Message.destroy();
          $MessageBox(
            "error",
            "US.COLLECTION.DEBTOR:ADDITIONAL_INFORMATION.DELETE_ADDITIONAL_INFORMATION_FAILED",
            "",
            ""
          );
        }
      } catch (error) {
        yield put(deleteInformation.fail(new Error()));
        $Message.destroy();
        $MessageBox(
          "error",
          "US.COLLECTION.DEBTOR:ADDITIONAL_INFORMATION.DELETE_ADDITIONAL_INFORMATION_FAILED",
          "",
          ""
        );
      }
    },
    getMetaDta: function* (action: any): any {
      try {
        const { category } = action?.payload?.data;
        const { data, status } = yield call(getMetaData);
        if (Array.isArray(data) && status === 200) {
          yield put(
            informationMetaData.success(
              formatAdditionalInfoMetaData(data, category)
            )
          );
        } else {
          yield put(informationMetaData.fail(new Error()));
        }
      } catch (error) {
        yield put(informationMetaData.fail(new Error()));
      }
    },
  },
  bmdDataTypeValues: {
    get: function* (action: any): any {
      const { dataTypeId } = action?.payload?.data ?? {};
      try {
        const {data , status} = yield call(BMD.getDataTypeValues, dataTypeId);
        if (status === 200 || status === 204) {
          yield put(bmdDataTypeValues.success({ dataTypeId, values: data }));
        } else {
          yield put(bmdDataTypeValues.success({ dataTypeId, values: [] }));
        }
      } catch (error) {
        yield put(bmdDataTypeValues.fail(new Error()));
      }
    },
  },
};

export default [
  takeLatest(
    GET_ADDITIONAL_INFORMATION,
    AdditionalInformationSagas.information.get
  ),
  takeLatest(
    NEW_ADDITIONAL_INFORMATION,
    AdditionalInformationSagas.information.new
  ),
  takeLatest(
    UPDATE_ADDITIONAL_INFORMATION,
    AdditionalInformationSagas.information.update
  ),
  takeLatest(
    DELETE_ADDITIONAL_INFORMATION,
    AdditionalInformationSagas.information.delete
  ),
  takeLatest(
    GET_INFORMATION_META_DATA,
    AdditionalInformationSagas.information.getMetaDta
  ),
  takeLatest(
    GET_DATA_TYPE_VALUES,
    AdditionalInformationSagas.bmdDataTypeValues.get
  ),
];
